//#region IMPORT
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Commons from '../../../Services/Common/Common';
import dayjs from 'dayjs';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import M_AIC_BASIC from './M_AIC_BASIC';
import {
  useSearch1,
  useSearch2,
  useSave1,
  useSave2,
} from './M_AIC_BASIC_Query';
import { NavBarInfoActions } from '../../../Reducer/NavBarInfoReducer';
import { useSave } from '../../../Hooks/useSave';
import useFileUpload from '../../../Hooks/useFileUpload';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import { useAppNavigate } from '../../../Hooks/back/useAppNavigate';
import { useBackStack } from '../../../Hooks/back/useBackStack';
//#endregion

function M_AIC_BASICF() {
  //#region 변수
  const { navigate } = useAppNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const [equipList, setEquipList] = useState([]);
  const [params1, setParams1] = useState([]);
  const [aiCallDate, setAiCallDate] = useState('');

  const [questionMessages, setQuestionMessages] = useState([]);
  const [aiCallMessage, setAiCallMessage] =
    useState('지시사항을 듣고 대답해주세요.');
  const [aiCallText, setAiCallText] = useState('');
  const [aiCallListIndex, setAiCallListIndex] = useState(0);
  const [ttsAudio, setTtsAudio] = useState([]);
  const [isReadyToSave, setIsReadyToSave] = useState(false);
  const [transcription, setTranscription] = useState('');
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const speechToTextRef = useRef(null);
  const recordedFileRef = useRef(null);

  const aiCallUpload = useFileUpload({
    oldFilePathField: 'voiceAddress',
    newFileNameField: 'saveName',
    getTargetPath: () =>
      `AI_VOICE_LOG/${userInfo.custCd}/${Commons.DecryptoAES(userInfo.userid)}`,
  });
  //#endregion
  // 스택형 뒤로가기로 변경: 보이스 체크 종료 시 상태에 따른 경로 이동
  const equipListRef = useRef(equipList);
  useEffect(() => {
    equipListRef.current = equipList;
  }, [equipList]);

  useBackStack({
    type: 'navigate',
    onBack: () => {
      // 규칙 10: ref를 통해 최신 equipList 확인 후 경로 결정(하드웨어 때문에 어긋남)
      const targetPath = equipListRef.current.length > 0 ? '/M_PPE' : '/Home';
      navigate(targetPath);
    },
  });
  //#region 이벤트
  useEffect(() => {
    dispatch(NavBarInfoActions.setType('titleBar'));
    dispatch(NavBarInfoActions.setTitleName('보이스 체크'));
    dispatch(NavBarInfoActions.setBgColor('#757B99'));

    let wakeLock;
    const requestWakeLock = async () => {
      try {
        wakeLock = await navigator.wakeLock?.request('screen');
      } catch (err) {
        console.error(`${err.name}, ${err.message}`);
      }
    };
    requestWakeLock();

    return () => {
      wakeLock?.release?.().catch(console.error);
      dispatch(NavBarInfoActions.setBgColor('#f7f8fa'));
    };
  }, [dispatch]);
  //#endregion

  //#region 쿼리
  const { refetch: Search1 } = useSearch1({
    onSuccess: (rows) => {
      if (rows.length > 0) {
        const questionMessage = rows.filter(
          (item) => item.gubun == 2 && item.useYn == 'Y'
        );
        setQuestionMessages(questionMessage);
        Commons.Storages('set', 'aiCallState', { startYn: 'Y' });
      }
    },
  });
  const { refetch: Search2 } = useSearch2({
    onSuccess: (rows) => {
      setEquipList(rows || []);
    },
  });
  const { runAsync: Save1Async } = useSave1(); // ai콜음성경로
  const { runAsync: Save2Async } = useSave2(); // 작업시작

  // 저장
  const { run: Save1 } = useSave({
    menuId: 'M_AIC_BASIC',
    action: 'SAVE_AI_CALL',
    fileSave: async () => {
      // FileChange 대신 createPreviewItem을 직접 호출(미리보기 파일만 생성)
      const item = await aiCallUpload.createPreviewItem(
        recordedFileRef.current
      );
      const finalPath = await aiCallUpload.SaveLogicFile([item]);

      return {
        voiceAddress: finalPath,
      };
    },
    requestSave: (rows) =>
      Save1Async([
        {
          voiceAddress:
            rows[0].voiceAddress ||
            `AI_VOICE_LOG/${userInfo.custCd}/${Commons.DecryptoAES(userInfo.userid)}/${recordedFileRef.current.name}`,
        },
      ]),
    onSuccess: () => {
      navigate('/Home');
    },
  });
  const { run: Save2 } = useSave({
    menuId: 'M_AIC_BASIC',
    requestSave: (rows) => Save2Async(rows),
    showSuccess: false,
  });
  //#endregion

  //#region 나머지 함수
  useEffect(() => {
    if (questionMessages.length > 0) {
      setAiCallMessage(questionMessages[0].message);
      setAiCallText(questionMessages[0].message);
    }
  }, [questionMessages]);

  const handleTranscriptionChange = (newTranscription) => {
    setTranscription(newTranscription);
    setAiCallDate(dayjs().format('YYYY-MM-DD HH:mm:ss'));
  };

  const handleAudioPlayEnd = () => {
    const isRecording = speechToTextRef.current?.isRecording?.();
    if (!isRecording) {
      speechToTextRef.current?.startRecording();
    }
  };

  useEffect(() => {
    if (transcription) {
      handleAnswerSubmit();
    }
  }, [transcription]);

  useEffect(() => {
    if (isReadyToSave) {
      userInfo?.internalYn !== '1' && Save2();
      MergeMultipleAudios(ttsAudio);
      Commons.Storages('set', 'aiCallState', { startYn: 'N', gubun: '' });
      setIsReadyToSave(false);
    }
  }, [params1, isReadyToSave]);

  const handleAnswerSubmit = () => {
    if (!transcription) return;
    const currentIndex = aiCallListIndex;
    const userAnswer = transcription.trim();
    const isLastQuestion = currentIndex >= questionMessages.length - 1;

    const normalizedAnswer =
      userAnswer === 'NULL1' || userAnswer === 'NULL2' ? '' : transcription;

    const newObj = {
      aiMessageCd: questionMessages[aiCallListIndex]?.aiMessageCd,
      aiCallDate: aiCallDate,
      answer: normalizedAnswer,
    };
    setParams1((prev) => [...prev, newObj]);

    if (isLastQuestion) {
      setIsReadyToSave(true);
    } else {
      const nextMessage = questionMessages[currentIndex + 1]?.message;
      setAiCallMessage(nextMessage);
      setAiCallText(nextMessage);
      setAiCallListIndex(currentIndex + 1);
    }
    setTranscription('');
  };

  useEffect(() => {
    const audioEl = audioRef.current;
    return () => {
      stopRecording();
      if (audioEl) {
        audioEl.pause();
        audioEl.currentTime = 0;
      }
    };
  }, []);

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
  };

  const MergeMultipleAudios = async (audioFiles) => {
    if (audioFiles.length < 2) {
      dispatch(
        showSnackbar({
          message: '병합할 오디오 파일이 2개 이상이어야 합니다.',
          type: 'info',
        })
      );
      Commons.Storages('set', 'aiCallState', { startYn: 'N', gubun: '' });
      navigate('/Home');
      return;
    }

    const ffmpeg = new FFmpeg();
    ffmpeg.on('log', () => {});
    await ffmpeg.load();

    for (let i = 0; i < audioFiles.length; i++) {
      const file = audioFiles[i].blob;
      const fileName = `audio${i}.mp3`;
      const fileBuffer = await fetchFile(file);
      await ffmpeg.writeFile(fileName, fileBuffer);
    }

    const inputFiles = audioFiles.map((_, i) => `-i audio${i}.mp3`).join(' ');
    const filterComplex =
      audioFiles.map((_, i) => `[${i}:a]`).join('') +
      `concat=n=${audioFiles.length}:v=0:a=1[out]`;
    const ffmpegCommand = [
      ...inputFiles.split(' '),
      '-filter_complex',
      filterComplex,
      '-map',
      '[out]',
      'output.mp3',
    ];

    const formattedDate = new Date()
      .toISOString()
      .slice(0, 16)
      .replace(/[-T:]/g, '');
    const fileName = formattedDate + '_S.mp3';

    try {
      await ffmpeg.exec(ffmpegCommand);
      const mergedAudioData = await ffmpeg.readFile('output.mp3');
      const mergedBlob = new Blob([mergedAudioData.buffer], {
        type: 'audio/mp3',
      });
      const file = new File([mergedBlob], fileName, { type: 'audio/mp3' });
      recordedFileRef.current = file;

      await Save1();
    } catch (e) {
      console.log(e);
      Commons.Storages('set', 'aiCallState', { startYn: 'N', gubun: '' });
      navigate('/Home');
    }
  };
  //#endregion

  return (
    <div className="wrap">
      <M_AIC_BASIC
        userInfo={userInfo}
        equipList={equipList}
        handleAudioPlayEnd={handleAudioPlayEnd}
        speechToTextRef={speechToTextRef}
        questionMessages={questionMessages}
        aiCallMessage={aiCallMessage}
        aiCallText={aiCallText}
        handleTranscriptionChange={handleTranscriptionChange}
        streamRef={streamRef}
        audioRef={audioRef}
        mediaRecorderRef={mediaRecorderRef}
        ttsAudio={ttsAudio}
        setTtsAudio={setTtsAudio}
      />
    </div>
  );
}

export default M_AIC_BASICF;
